import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Paper, Stack, TextField, Typography } from '@mui/material';
import apiService from '../services/api.service';

const money = (currency: string, value: any) => `${currency || 'SAR'} ${Number(value || 0).toFixed(2)}`;

const labelForAction = (action: string) => {
  const a = String(action || '').toLowerCase();
  if (a === 'approve') return 'Approve';
  if (a === 'reject') return 'Reject';
  if (a === 'rework') return 'Rework';
  return 'Review';
};

const POEmailAction: React.FC = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState('');
  const [success, setSuccess] = React.useState('');
  const [comment, setComment] = React.useState('');
  const [data, setData] = React.useState<any | null>(null);

  React.useEffect(() => {
    const run = async () => {
      if (!token) {
        setError('Invalid approval link');
        setLoading(false);
        return;
      }
      try {
        const res = await apiService.resolvePOApprovalActionToken(token);
        const d = (res as any)?.data || res || {};
        setData(d.data || d);
      } catch (e: any) {
        setError(String(e?.response?.data?.message || 'Unable to open approval link'));
      } finally {
        setLoading(false);
      }
    };
    void run();
  }, [token]);

  const handleConfirm = async () => {
    if (!token) return;
    if (data?.requiredComments && !String(comment || '').trim()) {
      setError(`Comment is required for ${labelForAction(data?.action).toLowerCase()}.`);
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const res = await apiService.processPOApprovalActionToken(token, comment);
      const d = (res as any)?.data || res || {};
      setSuccess(String(d.message || 'Action completed successfully'));
    } catch (e: any) {
      setError(String(e?.response?.data?.message || 'Failed to process action'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      component="main"
      id="main-content"
      sx={{
        minHeight: '100vh',
        '@supports (min-height: 100svh)': { minHeight: '100svh' },
        bgcolor: '#f8fafc',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: { xs: 1, sm: 2 },
      }}
    >
      <Paper sx={{ width: '100%', maxWidth: 880, p: { xs: 2, sm: 3 } }} aria-labelledby="poea-heading">
        {loading ? (
          <Stack spacing={2} alignItems="center" justifyContent="center" sx={{ minHeight: 240 }} role="status" aria-live="polite">
            <CircularProgress aria-label="Loading approval action" />
            <Typography variant="body2">Loading approval action...</Typography>
          </Stack>
        ) : (
          <Stack spacing={2}>
            <Typography
              component="h1"
              id="poea-heading"
              variant="h5"
              sx={{ fontWeight: 700, fontSize: { xs: 18, sm: 22, md: 24 } }}
            >
              PO {labelForAction(data?.action)}
            </Typography>
            {error ? <Alert severity="error">{error}</Alert> : null}
            {success ? <Alert severity="success">{success}</Alert> : null}
            {data ? (
              <Stack spacing={2}>
                <Box sx={{ p: 2, border: '1px solid #e2e8f0', borderRadius: 2, bgcolor: '#fff' }}>
                  <Typography variant="body2"><strong>PO Number:</strong> {data.poNumber}</Typography>
                  <Typography variant="body2"><strong>Project:</strong> {data.allocationCode || '-'}{data.projectName ? ` - ${data.projectName}` : ''}</Typography>
                  <Typography variant="body2"><strong>Supplier:</strong> {data.vendorCode ? `${data.vendorCode} - ${data.vendorName || ''}` : (data.vendorName || '-')}</Typography>
                  <Typography variant="body2"><strong>Required Date:</strong> {data.requiredDate ? new Date(data.requiredDate).toLocaleDateString() : '-'}</Typography>
                  <Typography variant="body2"><strong>Action:</strong> {labelForAction(data.action)}</Typography>
                  {data.remarks ? (
                    <Typography variant="body2"><strong>Remarks:</strong> {data.remarks}</Typography>
                  ) : null}
                </Box>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
                    gap: 1.5
                  }}
                >
                  {[
                    { label: 'PO TOTAL', value: money(data.currency, data.totalAmount), color: '#0f172a' },
                    { label: 'ESTIMATED COST', value: money(data.currency, data.estimatedCost), color: '#0f172a' },
                    { label: 'PROFIT MARGIN', value: money(data.currency, data.profitAmount), color: Number(data.profitAmount || 0) >= 0 ? '#166534' : '#b91c1c' },
                    { label: 'MARGIN %', value: `${Number(data.marginPct || 0).toFixed(2)}%`, color: Number(data.marginPct || 0) >= 0 ? '#166534' : '#b91c1c' }
                  ].map((card) => (
                    <Box key={card.label} sx={{ p: 2, border: '1px solid #dbe2ea', borderRadius: 2, bgcolor: '#fff' }}>
                      <Typography variant="caption" sx={{ fontWeight: 800, color: '#64748b', letterSpacing: 0.4 }}>
                        {card.label}
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 800, color: card.color, mt: 0.5 }}>
                        {card.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Stack>
            ) : null}
            {data?.requiredComments ? (
              <TextField
                label="Comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                multiline
                minRows={3}
                fullWidth
              />
            ) : null}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Button variant="contained" onClick={handleConfirm} disabled={submitting || !!success}>
                {submitting ? 'Processing...' : `Confirm ${labelForAction(data?.action)}`}
              </Button>
              {data?.poid ? (
                <Button variant="outlined" onClick={() => navigate(`/po/${Number(data.poid)}`)}>
                  Open PO
                </Button>
              ) : null}
            </Stack>
            <Typography variant="caption" color="text.secondary">
              This page confirms the action securely. Opening the email alone does not change approval status.
            </Typography>
          </Stack>
        )}
      </Paper>
    </Box>
  );
};

export default POEmailAction;
